import { Box, Button, Heading, SegmentGroup, Stack, Switch, Text } from '@chakra-ui/react'
import { FiMail } from 'react-icons/fi'
import { useNavigate } from 'react-router-dom'
import { PageContainer } from '../components/shared/PageContainer'
import { Card } from '../components/shared/Card'
import { useAuth } from '../hooks/useAuth'
import { appearancePreferences, type PreferenceEntry, type PreferenceKey } from '../preferences/registry'
import { usePreference } from '../preferences/usePreference'

function PreferenceControl({ entry }: { entry: PreferenceEntry }) {
  const [value, setValue] = usePreference(entry.key as PreferenceKey)

  if (entry.type === 'boolean') {
    return (
      <Switch.Root
        checked={value === true}
        onCheckedChange={(e) => setValue(e.checked === true)}
        disabled={entry.disabled}
        colorPalette="brand"
      >
        <Switch.HiddenInput />
        <Switch.Control>
          <Switch.Thumb />
        </Switch.Control>
      </Switch.Root>
    )
  }

  // enum
  return (
    <SegmentGroup.Root
      value={String(value)}
      onValueChange={(e) => { if (e.value) setValue(e.value) }}
      disabled={entry.disabled}
      colorPalette="brand"
      size="sm"
    >
      <SegmentGroup.Indicator />
      <SegmentGroup.Items items={(entry.options ?? []).map((o) => ({ value: o.value, label: o.label }))} />
    </SegmentGroup.Root>
  )
}

function PreferenceRow({ entry }: { entry: PreferenceEntry }) {
  return (
    <Box
      data-testid={`pref-control-${entry.key}`}
      display="flex"
      flexDirection={{ base: 'column', md: 'row' }}
      alignItems={{ base: 'stretch', md: 'center' }}
      justifyContent="space-between"
      gap={3}
      py={3}
    >
      <Box>
        <Text fontWeight="500" color="ink">{entry.label}</Text>
        <Text fontSize="sm" color="muted">{entry.description}</Text>
      </Box>
      <Box flexShrink={0}>
        <PreferenceControl entry={entry} />
      </Box>
    </Box>
  )
}

export function SettingsPage() {
  const appearance = appearancePreferences()
  const { isOrganizer } = useAuth()
  const navigate = useNavigate()

  return (
    <PageContainer title="Settings" subtitle="Personalize how ORCL looks on this device.">
      <Card>
        <Heading size="md" mb={1}>Appearance</Heading>
        <Text fontSize="sm" color="muted" mb={2}>Saved on this device only.</Text>
        <Stack gap={0} separator={<Box borderTopWidth="1px" borderColor="border" />}>
          {appearance.map((entry) => (
            <PreferenceRow key={entry.key} entry={entry} />
          ))}
        </Stack>
      </Card>

      {/* This page has no route-level guard (any signed-in role lands here), unlike the
          suppressions page itself (OrganizerGuard) -- so the link is gated client-side to
          Organizer/Admin, matching every other client-side role gate in this app
          (frontend/src/CLAUDE.md's "Security" note). */}
      {isOrganizer && (
        <Card mt={6}>
          <Heading size="md" mb={1}>Communications</Heading>
          <Text fontSize="sm" color="muted" mb={3}>
            Manage addresses that will never receive email from this committee.
          </Text>
          <Button
            variant="outline"
            onClick={() => navigate('/dashboard/settings/communications/suppressions')}
          >
            <FiMail />Email suppressions
          </Button>
        </Card>
      )}
    </PageContainer>
  )
}
